import React, { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { showSuccess, showError, showWarning } from "../utils/toastUtils";
import ConfirmModal from "../utils/ConfirmModal";
import "../stili/Categorie.css";

const API_URL = process.env.REACT_APP_API_URL;

const MACRO_GRUPPI = [
  { value: "SCUOLA_CALCIO", label: "Scuola Calcio" },
  { value: "AGONISTICA", label: "Agonistica" },
];

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    background: "rgba(255,255,255,0.10)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    borderColor: state.isFocused
      ? "rgba(255,255,255,0.40)"
      : "rgba(255,255,255,0.18)",
    boxShadow: "none",
    cursor: "pointer",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    background: "rgba(15, 23, 42, 0.96)",
    border: "1px solid rgba(255,255,255,0.16)",
    zIndex: 30,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#fff",
  }),
  input: (base) => ({
    ...base,
    color: "#fff",
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(255,255,255,0.75)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "rgba(255,255,255,0.12)"
      : "transparent",
    color: "#fff",
    cursor: "pointer",
  }),
};

function formatMacroGruppo(value) {
  if (value === "SCUOLA_CALCIO") return "Scuola Calcio";
  if (value === "AGONISTICA") return "Agonistica";
  return "-";
}

export default function CategorieForm() {
  const [categorie, setCategorie] = useState([]);
  const [nomeCategoria, setNomeCategoria] = useState("");
  const [macroGruppo, setMacroGruppo] = useState("");
  const [categoriaInModifica, setCategoriaInModifica] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoriaDaEliminare, setCategoriaDaEliminare] = useState(null);

  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const fetchCategorie = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/categorie`);

      if (res.status === 403) {
        showError("Accesso negato a categorie.");
        return;
      }

      if (!res.ok) {
        throw new Error("Errore nel caricamento delle categorie");
      }

      const data = await res.json().catch(() => []);
      setCategorie(Array.isArray(data) ? data : []);
    } catch (err) {
      showError(err.message || "Errore nel caricamento delle categorie.");
      setCategorie([]);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchCategorie();
  }, [fetchCategorie]);

  const resetForm = () => {
    setNomeCategoria("");
    setMacroGruppo("");
    setCategoriaInModifica(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!nomeCategoria.trim()) {
      showWarning("Inserisci un nome valido.");
      return;
    }

    if (!macroGruppo) {
      showWarning("Seleziona il macro gruppo.");
      return;
    }

    try {
      const method = categoriaInModifica ? "PUT" : "POST";
      const url = categoriaInModifica
        ? `${API_URL}/api/admin/categorie/${categoriaInModifica.id}`
        : `${API_URL}/api/admin/categorie`;

      const payload = {
        nome: nomeCategoria.trim().toUpperCase(),
        macroGruppo,
      };

      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.status === 403) {
        showError("Accesso negato.");
        return;
      }

      if (!res.ok) {
        let msg = "Errore durante il salvataggio";
        try {
          const errJson = await res.json();
          msg = errJson.message || errJson.error || msg;
        } catch {}
        throw new Error(msg);
      }

      showSuccess(
        categoriaInModifica
          ? "Categoria aggiornata con successo."
          : "Categoria salvata con successo."
      );

      resetForm();
      fetchCategorie();
    } catch (err) {
      showError(err.message || "Errore nel salvataggio della categoria.");
    }
  };

  const handleEdit = (categoria) => {
    setCategoriaInModifica(categoria);
    setNomeCategoria((categoria.nome || "").toUpperCase());
    setMacroGruppo(categoria.macroGruppo || "");
  };

  const handleDeleteClick = (id) => {
    setCategoriaDaEliminare(id);
    setDeleteModalOpen(true);
  };

  const confermaEliminazione = async () => {
    if (!categoriaDaEliminare) return;

    try {
      const res = await authFetch(`${API_URL}/api/admin/categorie/${categoriaDaEliminare}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        let msg = "Errore eliminazione categoria";
        try {
          const errJson = await res.json();
          msg = errJson.message || errJson.error || msg;
        } catch {}
        throw new Error(msg);
      }

      showSuccess("Categoria eliminata con successo.");
      setDeleteModalOpen(false);
      setCategoriaDaEliminare(null);

      if (categoriaInModifica?.id === categoriaDaEliminare) {
        resetForm();
      }

      fetchCategorie();
    } catch (err) {
      showError(err.message || "Errore durante l'eliminazione.");
      setDeleteModalOpen(false);
      setCategoriaDaEliminare(null);
    }
  };

  const annullaEliminazione = () => {
    setDeleteModalOpen(false);
    setCategoriaDaEliminare(null);
    showWarning("Eliminazione annullata.");
  };

  const macroGruppoSelected =
    MACRO_GRUPPI.find((m) => m.value === macroGruppo) || null;

  return (
    <div className="categorie-container" translate="no">
      <h2 className="categorie-title">Gestione Categorie</h2>

      <div className="categorie-card">
        <div className="categorie-section">
          <h3 className="categorie-section-title">
            {categoriaInModifica ? "Modifica categoria" : "Nuova categoria"}
          </h3>

          <form onSubmit={handleSubmit}>
            <div className="categorie-grid-2">
              <div className="categorie-form-group">
                <label className="categorie-label">Inserisci la Categoria</label>
                <input
                  className="categorie-input"
                  type="text"
                  value={nomeCategoria}
                  onChange={(e) => setNomeCategoria(e.target.value.toUpperCase())}
                  placeholder="Es. 2015"
                  required
                />
              </div>

              <div className="categorie-form-group">
                <label className="categorie-label">Macro gruppo</label>
                <Select
                  options={MACRO_GRUPPI}
                  value={macroGruppoSelected}
                  onChange={(selected) => setMacroGruppo(selected?.value || "")}
                  placeholder="Seleziona macro gruppo"
                  isClearable
                  styles={customSelectStyles}
                />
              </div>
            </div>

            <div className="categorie-actions">
              <button type="submit" className="categorie-btn-primary">
                {categoriaInModifica ? "Modifica Categoria" : "Salva Categoria"}
              </button>

              {categoriaInModifica && (
                <button
                  type="button"
                  className="categorie-btn-secondary"
                  onClick={resetForm}
                >
                  Annulla Modifica
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {categorie.length === 0 ? (
        <div className="categorie-empty-state">Nessuna categoria presente.</div>
      ) : (
        <div className="categorie-cards-grid">
          {categorie.map((cat) => (
            <article className="categorie-item-card" key={cat.id}>
              <div className="categorie-item-header">
                <h3 className="categorie-item-title">{cat.nome}</h3>
                <p className="categorie-item-meta">
                  Macro gruppo: {formatMacroGruppo(cat.macroGruppo)}
                </p>
              </div>

              <div className="categorie-actions">
                <button
                  type="button"
                  className="categorie-btn-primary"
                  onClick={() => handleEdit(cat)}
                >
                  Modifica
                </button>

                <button
                  type="button"
                  className="categorie-btn-danger"
                  onClick={() => handleDeleteClick(cat.id)}
                >
                  Elimina
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="categorie-button-row">
        <button
          type="button"
          className="categorie-btn-secondary"
          onClick={() => navigate("/gestionale")}
        >
          Torna al Gestionale
        </button>
      </div>

      <ConfirmModal
        open={deleteModalOpen}
        title="Elimina categoria"
        message="Sei sicuro di voler eliminare questa categoria? L'operazione non può essere annullata."
        confirmText="Elimina"
        cancelText="Annulla"
        danger={true}
        onConfirm={confermaEliminazione}
        onCancel={annullaEliminazione}
      />
    </div>
  );
}